import React, { useEffect, useMemo, useRef, useState } from "react";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function OtpInputs({ value, onChange }) {
  const inputsRef = useRef([]);

  const digits = useMemo(() => {
    const arr = new Array(6).fill("");
    const cleaned = (value || "").replace(/\D/g, "").slice(0, 6);
    for (let i = 0; i < cleaned.length; i += 1) arr[i] = cleaned[i];
    return arr;
  }, [value]);

  function setAt(idx, ch) {
    const next = [...digits];
    next[idx] = (ch || "").replace(/\D/g, "").slice(0, 1);
    onChange(next.join("").replace(/\D/g, "").slice(0, 6));
  }

  return (
    <div className="flex justify-center gap-2">
      {digits.map((d, idx) => (
        <input
          key={idx}
          ref={(el) => (inputsRef.current[idx] = el)}
          value={d}
          inputMode="numeric"
          autoComplete="one-time-code"
          onChange={(e) => {
            const ch = e.target.value;
            setAt(idx, ch);
            if (ch && idx < 5) inputsRef.current[idx + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !digits[idx] && idx > 0) {
              inputsRef.current[idx - 1]?.focus();
            }
            if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
            if (e.key === "ArrowRight" && idx < 5) inputsRef.current[idx + 1]?.focus();
          }}
          className="h-12 w-10 rounded-xl border border-slate-200 bg-white text-center text-lg font-semibold text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
        />
      ))}
    </div>
  );
}

function SuccessIcon() {
  return (
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
      <svg
        viewBox="0 0 24 24"
        className="h-8 w-8 text-emerald-500"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M20 6L9 17l-5-5" />
      </svg>
    </div>
  );
}

export default function AuthOverlay({ open, initialMode = "login", onClose }) {
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState("form"); // form | otp | success
  const [otp, setOtp] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setStep("form");
    setOtp("");
  }, [open, initialMode]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (!open) return undefined;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isLogin = mode === "login";
  const primaryLabel =
    step === "form" ? (isLogin ? "Login" : "Sign Up") : step === "otp" ? "Validate OTP" : "Go to Home";

  function submitForm(e) {
    e.preventDefault();
    setStep("otp");
  }

  function submitOtp(e) {
    e.preventDefault();
    setStep("success");
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close authentication modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-xl">
          <div className="relative flex items-center justify-center px-6 pt-5">

            {/* Center Title */}
            <p className="text-2xl font-semibold text-slate-800">
              {step === "form"
                ? isLogin
                  ? "Sign In"
                  : "Sign Up"
                : step === "otp"
                  ? "Validate OTP"
                  : "Verified"}
            </p>

            {/* Right Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="absolute right-6 rounded-full p-2 text-slate-500 hover:bg-slate-100"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* {step === "form" && (
            <div className="px-6 pt-4">
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className={cx(
                    "rounded-lg px-3 py-2 font-semibold transition",
                    mode === "login" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className={cx(
                    "rounded-lg px-3 py-2 font-semibold transition",
                    mode === "signup" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  Sign up
                </button>
              </div>
            </div>
          )} */}

          <div className="px-6 pb-6 pt-4">
            {step === "success" ? (
              <div className="text-center">
                <SuccessIcon />
                <h3 className="mt-4 text-lg font-semibold text-slate-900">Congratulations</h3>
                <p className="mt-1 text-sm text-slate-500">Your account is verified successfully.</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-6 w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  {primaryLabel}
                </button>
              </div>
            ) : step === "otp" ? (
              <form onSubmit={submitOtp} className="space-y-5">
                <p className="text-sm text-slate-600">
                  Enter the 6-digit code sent to{" "}
                  <span className="font-semibold text-slate-900">{form.email || "your email"}</span>.
                </p>
                <OtpInputs value={otp} onChange={setOtp} />
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <button type="button" className="font-semibold text-indigo-600 hover:text-indigo-700">
                    Resend code
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("form");
                      setOtp("");
                    }}
                    className="hover:text-slate-700"
                  >
                    Change details
                  </button>
                </div>
                <button
                  type="submit"
                  className="w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={(otp || "").replace(/\D/g, "").length !== 6}
                >
                  {primaryLabel}
                </button>
              </form>
            ) : (
              <form onSubmit={submitForm} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Full name
                  </label>
                  <input
                    value={form.name}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, name: e.target.value }))
                    }
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                    placeholder="John Doe"
                  />
                </div>
              )}
            
              <div>
                <label className="text-xs font-semibold text-slate-700">
                  Email
                </label>
                <input
                  value={form.email}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, email: e.target.value }))
                  }
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="you@example.com"
                  type="email"
                />
              </div>
            
              <div>
                <label className="text-xs font-semibold text-slate-700">
                  Password
                </label>
                <input
                  value={form.password}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, password: e.target.value }))
                  }
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="••••••••"
                  type="password"
                />
              </div>
            
              {!isLogin && (
                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Confirm password
                  </label>
                  <input
                    value={form.confirmPassword}
                    onChange={(e) =>
                      setForm((s) => ({
                        ...s,
                        confirmPassword: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                    placeholder="••••••••"
                    type="password"
                  />
                </div>
              )}
            
              {/* Login Button */}
              <button
                type="submit"
                className="w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                {primaryLabel} →
              </button>
            
              {/* Forgot Password (Only for Login) */}
              {isLogin && (
                <div className="text-right">
                  <button
                    type="button"
                    className="text-sm text-slate-700 hover:text-indigo-600"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            
              {/* Switch Login / Signup */}
              <div className="text-sm text-slate-500 text-center">
                {isLogin ? (
                  <>
                    Don’t have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("signup")}
                      className="font-semibold text-slate-900 hover:text-indigo-600"
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      className="font-semibold text-slate-900 hover:text-indigo-600"
                    >
                      Sign in
                    </button>
                  </>
                )}
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

